import { GuestSessionNotice } from "@/components/profile/GuestSessionNotice"
import { basicInfoAtom, cartAtom, profileAtom } from "@/atoms"
import { endBirthdayOrderAttemptAtom } from "@/atoms/birthday-prompt"
import FinishOrderButton from "@/components/cart/confirm-order/FinishOrderButton"
import { PaymentMethod as PaymentMethodLabel } from "@/components/orders/PaymentMethod"
import { Armchair, ChatCircleText, Edit, Pin, Receipt, ShoppingBag, Tag, Wallet, type IconGlyph } from "@/icons"
import { formatBRL } from "@/lib/format-brl"
import { STAGGER, TRANSITION } from "@/lib/motion"
import type { Address } from "@/types"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { motion, useReducedMotion, type Variants } from "motion/react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useCheckoutContext } from "../CheckoutContext"
import type { OrderConfirmedState } from "../OrderConfirmedPage"
import { SelectedItemsSummary } from "../components/SelectedItemsSheet"
import { StepFooter } from "../components/StepFooter"
import { StepHeader } from "../components/StepHeader"
import { useEnforceWizardEntry } from "../hooks/useEnforceWizardEntry"
import { useOrderUpdatesChoice } from "../hooks/useOrderUpdatesChoice"
import { checkoutAtom, computePixServiceFee } from "../state"
import { useDiscountPlan } from "../hooks/useDiscountPlan"
import { appliedCouponLines } from "../lib/applied-coupons"
import { CheckoutCouponSummary } from "../components/coupons/CheckoutCouponSummary"
import { LoyaltySourceList } from "../components/loyalty/LoyaltySourceList"
import { loyaltySourceRows } from "../lib/loyalty-source"
import { OrderUpdatesPreference } from "../components/OrderUpdatesPreference"
import {
  AddressSummary,
  Button,
  ButtonAnchor,
  Card,
  Collapse,
  DescriptionList,
  Field,
  Icon,
  KeyValueRow,
  PageContainer,
  Price,
  SectionHeader,
  Text,
  TextArea,
} from "@/components/ui"
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling"
import { formatArrivalCommitment, formatScheduledFor } from "@/lib/scheduling/format"
import { getStoredCompany } from "@/lib/company-storage"
import { forgetKeptCoupons } from "@/lib/pending-coupon"

function buildMapsUrl(address: Address): string {
  if (address.latitude && address.longitude) {
    return `https://www.google.com/maps/search/?api=1&query=${address.latitude},${address.longitude}`
  }
  const query = encodeURIComponent(
    `${address.street}, ${address.number} - ${address.district}, ${address.city} - ${address.state}`
  )
  return `https://www.google.com/maps/search/?api=1&query=${query}`
}

export function ReviewStep() {
  const { timezone: schedulingTimezone } = useStorefrontScheduling()
  const { addresses, hasExtras, isTableOrder, tableName, tableId, tableExternalCode } = useCheckoutContext()
  const cart = useAtomValue(cartAtom)
  const profile = useAtomValue(profileAtom)
  const basicInfo = useAtomValue(basicInfoAtom)
  const [state, setState] = useAtom(checkoutAtom)
  const endBirthdayOrderAttempt = useSetAtom(endBirthdayOrderAttemptAtom)
  const navigate = useNavigate()
  const reduceMotion = useReducedMotion()
  const [notesExpanded, setNotesExpanded] = useState(false)
  const plan = useDiscountPlan()
  const orderUpdates = useOrderUpdatesChoice()
  const notesOpen = notesExpanded || state.specialInstructions.length > 0

  useEnforceWizardEntry("review", addresses)

  if (!profile) return null

  const { benefitDiscounts, couponDiscounts, couponDiscount, loyaltyDiscount, pointsUsed, total } = plan.pricing
  const loyaltyRows = loyaltySourceRows({
    benefitDiscounts,
    pointsUsed,
    pointsDiscount: loyaltyDiscount,
    active: state.benefits.active,
    grants: state.benefits.available,
    rewards: state.rewards,
    pointsBalance: state.loyalty.pointsBalance,
    rulePromotions: plan.rulePromotions.data,
  })
  const appliedCoupons = appliedCouponLines(couponDiscounts)

  function forgetUsedCoupons() {
    const merchantId = getStoredCompany()?.id
    if (merchantId)
      forgetKeptCoupons(
        appliedCoupons.map((coupon) => coupon.code),
        merchantId
      )
  }
  const evaluating = plan.coupons.phase === "checking" || plan.rulePromotions.phase === "checking"
  const selectedAddress = addresses.find((a) => a.id === state.addressId)
  const isPickup = state.serviceType !== "DELIVERY"
  const storeAddress = basicInfo?.address

  const listVariants: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: reduceMotion ? 0 : STAGGER.base } },
  }
  const cardVariants: Variants = {
    hidden: reduceMotion ? { opacity: 0 } : { opacity: 0, y: 14 },
    show: {
      opacity: 1,
      y: 0,
      transition: TRANSITION.enter,
    },
  }

  return (
    <>
      <StepHeader
        title="Confira e finalize seu pedido"
        description="Revise tudo antes de confirmar. Se algo estiver errado, toque em editar."
      />

      <PageContainer className="pt-4">
        <GuestSessionNotice />
      </PageContainer>
      <PageContainer as="section" className="pt-4">
        <motion.div className="flex flex-col gap-3" variants={listVariants} initial="hidden" animate="show">
          <SummaryCard
            title="Itens selecionados"
            icon={ShoppingBag}
            onEdit={() => navigate("/cart")}
            variants={cardVariants}
          >
            <SelectedItemsSummary cart={cart} />
          </SummaryCard>

          <SummaryCard
            title={isTableOrder ? "Mesa" : "Como receber"}
            icon={isTableOrder ? Armchair : Pin}
            onEdit={isTableOrder ? undefined : () => navigate("/cart/checkout/service")}
            variants={cardVariants}
          >
            <div className="min-w-0 flex-1">
              {isTableOrder ? (
                <Text weight="semibold" tone="default">
                  {tableName || "Mesa"}
                </Text>
              ) : (
                <>
                  <Text weight="semibold" tone="default">
                    {state.serviceType === "DELIVERY" ? "Entrega no endereço" : "Retirar na loja"}
                  </Text>
                  {state.scheduledArrival && state.serviceType ? (
                    <ScheduledLine onEdit={() => navigate("/cart/checkout/schedule")}>
                      {formatArrivalCommitment(
                        { arrivalStart: state.scheduledArrival.start, arrivalEnd: state.scheduledArrival.end },
                        state.serviceType,
                        schedulingTimezone ?? undefined
                      )}
                    </ScheduledLine>
                  ) : state.scheduledSlot ? (
                    <ScheduledLine onEdit={() => navigate("/cart/checkout/schedule")}>
                      {formatScheduledFor(state.scheduledSlot.readyAt, new Date(), schedulingTimezone ?? undefined)}
                    </ScheduledLine>
                  ) : null}
                  {state.serviceType === "DELIVERY" && selectedAddress ? (
                    <AddressSummary address={selectedAddress} className="mt-1" />
                  ) : null}
                  {isPickup && storeAddress ? (
                    <>
                      <AddressSummary address={storeAddress} className="mt-1" />
                      <ButtonAnchor
                        href={buildMapsUrl(storeAddress)}
                        target="_blank"
                        variant="outline"
                        tone="neutral"
                        size="sm"
                        className="mt-3"
                      >
                        <Icon as={Pin} size="md" tone="primary" />
                        Ver no mapa
                      </ButtonAnchor>
                    </>
                  ) : null}
                </>
              )}
            </div>
          </SummaryCard>

          {!isTableOrder && (
            <SummaryCard
              title="Forma de pagamento"
              icon={Wallet}
              onEdit={() => navigate("/cart/checkout/payment")}
              variants={cardVariants}
            >
              <div>
                {state.paymentMethod ? (
                  <Text weight="semibold" tone="default">
                    <PaymentMethodLabel paymentMethod={state.paymentMethod} />
                  </Text>
                ) : null}
                {state.paymentMethod === "CASH" && state.changeFor != null ? (
                  <Text size="base" tone="muted" className="mt-1">
                    Troco para {formatBRL(state.changeFor)}
                  </Text>
                ) : null}
              </div>
            </SummaryCard>
          )}

          {(appliedCoupons.length > 0 || state.couponCodes.length > 0 || loyaltyRows.length > 0) && (
            <SummaryCard
              title="Descontos aplicados"
              icon={Tag}
              onEdit={() => navigate("/cart/checkout/extras")}
              variants={cardVariants}
            >
              <div className="flex flex-col gap-1.5" aria-busy={evaluating}>
                <CheckoutCouponSummary />
                {plan.coupons.phase === "error" ? (
                  <Text size="sm" tone="danger">
                    Não conseguimos conferir seus cupons agora. O desconto não entra no total.
                  </Text>
                ) : null}
                {loyaltyRows.length > 0 ? <LoyaltySourceList rows={loyaltyRows} /> : null}
              </div>
            </SummaryCard>
          )}

          <SummaryCard title="Observações" icon={ChatCircleText} variants={cardVariants}>
            {!notesOpen ? (
              <Button
                variant="dashed"
                tone="neutral"
                size="row"
                align="start"
                weight="semibold"
                fullWidth
                onClick={() => setNotesExpanded(true)}
              >
                <Icon as={ChatCircleText} size="md" tone="primary" />
                Adicionar observação
              </Button>
            ) : null}
            <Collapse open={notesOpen}>
              <Field
                id="special-instructions"
                label="Ex: sem cebola, ponto da carne, troco específico, ponto de referência."
                counter={{ current: state.specialInstructions.length, max: 200 }}
              >
                <TextArea
                  autoFocus={notesExpanded}
                  inputSize="sm"
                  className="h-24"
                  placeholder="Escreva sua observação aqui"
                  maxLength={200}
                  value={state.specialInstructions}
                  onChange={(event) => setState((prev) => ({ ...prev, specialInstructions: event.target.value }))}
                />
              </Field>
            </Collapse>
          </SummaryCard>

          <OrderUpdatesPreference checked={orderUpdates.notifyStatusWhatsapp} onCheckedChange={orderUpdates.change} />

          <SummaryCard title="Resumo dos valores" icon={Receipt} variants={cardVariants}>
            <DescriptionList>
              <KeyValueRow label="Subtotal" value={<Price value={cart.totalPrice} />} />
              {computePixServiceFee(state) > 0 ? (
                <KeyValueRow label="Taxa de serviço" value={<Price value={computePixServiceFee(state)} />} />
              ) : null}
              {!isTableOrder && (
                <KeyValueRow label="Taxa de entrega" value={<Price value={state.servicePrice ?? 0} free />} />
              )}
              {couponDiscount > 0 ? (
                <KeyValueRow
                  label={appliedCoupons.length > 1 ? "Cupons" : "Cupom"}
                  value={<Price value={couponDiscount} sign="minus" tone="secondary" />}
                />
              ) : null}
              {loyaltyRows.map((row) => (
                <KeyValueRow
                  key={row.key}
                  label={row.summaryLabel}
                  value={<Price value={row.value} sign="minus" tone="secondary" />}
                />
              ))}
              <KeyValueRow
                emphasis="total"
                label="Total"
                value={<Price value={total} loading={evaluating} tone="secondary" />}
              />
            </DescriptionList>
          </SummaryCard>
        </motion.div>
      </PageContainer>

      <StepFooter
        onBack={() => {
          if (isTableOrder) {
            navigate("/cart", { replace: true })
            return
          }
          navigate(hasExtras ? "/cart/checkout/extras" : "/cart/checkout/payment", {
            replace: true,
          })
        }}
        rightSlot={
          <div className="flex-1 min-w-0">
            <FinishOrderButton
              profile={profile}
              serviceType={state.serviceType}
              orderTiming={state.orderTiming}
              scheduledSlot={state.scheduledSlot}
              scheduledArrival={state.scheduledArrival}
              paymentMethod={state.paymentMethod}
              methodInfo={state.selectedMethodInfo}
              paymentMethodId={state.selectedPaymentMethodId}
              specialInstructions={state.specialInstructions}
              servicePrice={state.servicePrice}
              pixServiceFee={computePixServiceFee(state)}
              couponDiscounts={couponDiscounts}
              disabled={evaluating}
              changeFor={state.changeFor ?? 0}
              cart={cart}
              address={selectedAddress}
              loyaltyPoints={loyaltyDiscount}
              benefitDiscounts={benefitDiscounts}
              isTableOrder={isTableOrder}
              tableId={tableId}
              tableExternalCode={tableExternalCode}
              notifyStatusWhatsapp={orderUpdates.notifyStatusWhatsapp}
              onOrderPlaced={orderUpdates.persist}
              onDeliveryFeeChanged={(fee) => setState((prev) => ({ ...prev, servicePrice: fee }))}
              onOrderConfirmed={(orderId, scheduledSummary) => {
                forgetUsedCoupons()
                endBirthdayOrderAttempt()
                navigate(`/cart/order-confirmed/${orderId}`, {
                  replace: true,
                  state: { scheduledSummary } satisfies OrderConfirmedState,
                })
              }}
              onPixClosed={(orderId) => {
                forgetUsedCoupons()
                endBirthdayOrderAttempt()
                navigate(`/profile/orders/${orderId}`, { replace: true })
              }}
              buttonLabel="Finalizar pedido"
              containerClassName="w-full"
            />
          </div>
        }
      />
    </>
  )
}

type ScheduledLineProps = {
  onEdit: () => void
  children: React.ReactNode
}

function ScheduledLine({ onEdit, children }: ScheduledLineProps) {
  return (
    <Text size="base" weight="semibold" tone="secondary">
      {children}
      <Button variant="link" size="auto" weight="semibold" onClick={onEdit} className="ml-2 align-baseline">
        <Text as="span" size="sm">
          Editar horário
        </Text>
      </Button>
    </Text>
  )
}

type SummaryCardProps = {
  title: string
  onEdit?: () => void
  icon?: IconGlyph
  variants?: Variants
  children: React.ReactNode
}

function SummaryCard({ title, onEdit, icon, variants, children }: SummaryCardProps) {
  return (
    <motion.div variants={variants}>
      <Card padding="lg">
        <SectionHeader
          title={title}
          icon={icon}
          action={
            onEdit ? (
              <Button variant="ghost" size="sm" onClick={onEdit}>
                <Icon as={Edit} size="xs" />
                Editar
              </Button>
            ) : undefined
          }
        />
        {children}
      </Card>
    </motion.div>
  )
}
